import React from "react";
import { Hero, PageContent } from "../../components";

function Description() {
  return (
    <div className="flex overflow-x-hidden overflow-y-hidden relative flex-col z-8">
      <Hero title={"Description"}>
        Climate crisis is a battle we must win for the sake of our planet and{" "}
        <span className="text-lg font-black text-RougePastel md:text-xl lg:text-2xl">
          future generations.
        </span>
      </Hero>

      <PageContent>
        {`
# The birth of Clean Heat

**Ionis-Paris** is deeply concerned about the energy crisis. From day one, we are dedicated to developing initiatives that not only address the challenges posed by the energy crisis but also make a meaningful, positive impact on the environment.

We were truly inspired by the thermogenic power of plants and especially by the *Symplocarpus renifolius*, also known as the skunk cabbage. Its name is quite literal: it produces a very strong smell of rotten meat when it is getting warmer to attract pollinators. In fact, this cabbage can maintain its temperature at 23°C, even in negative temperatures, and so being able to produce a differential of 35 °C [1].

<div class="image_frame">
    <img class="illustration medium" src="https://static.igem.wiki/teams/4577/wiki/project/description/skunk-cabbage-1.jpg">
</div>

What if we apply this capacity to a heating device? The recent energy crisis deeply affected us and everyone around us last winter. Motivated by this challenge, we brainstormed ways to utilize household waste for heating purposes. Conversations then shifted towards wastewater and the potential to use it as a growth medium for specially engineered microorganisms modified with the identified thermogenic protein.

Additionally, our discussions shed light on the environmental concerns tied to household water usage. We discovered that many pollutants, including endocrine disruptors, stem from the water we use in our homes.

**What if a heating system would not only heat but depollute water as well?**

This is what **Clean Heat** offers: this innovative project aims at developing a new low-environmental impact and bio-based heating system while allowing the bioremediation of estrogenic hormones.

## Context

Green energy production is one of the major challenges of the 21st century. Indeed, the climate crisis coupled with the complex socio-economic situation highlights an urgent need to develop cleaner and greener energy systems. Heating represents 80% of direct CO2 emissions of buildings and thus represents a major source of pollution in France, as 65% is generated via the combustion of fossil fuels [2]. Moreover, households have difficulties meeting both ends since heating stands for 19 % of the total energy consumed in France and 60% of household energy consumption according to the French Ecological Transition Agency.

<div class="image_frame">
    <img class="illustration medium" src="https://static.igem.wiki/teams/4577/wiki/project/description/figure-1.png">
    <p><b>Figure 1: The main sources of heating in France in 2017 (Insee)</b></p>
</div>

At the same time, endocrine disruptors pervade various aspects of our surroundings, contaminating the environment and posing a significant threat to our well-being. 150 million of women in the world, and 71% in France use contraceptive pills based on synthetic estrogens, also called 17-alpha ethinylestradiols (EE2) [3,4]. These micro-pollutants are found at a concentration of 2.2 ng/L in the water reserve in Europe, while only a low concentration of 0.32 ng/L shows effects, up to a loss of fertility of the aquatic life and carcinogenic danger for human health [5].

<div class="image_frame">
    <img class="illustration medium" src="https://static.igem.wiki/teams/4577/wiki/project/description/figure-2.png">
    <p><b>Figure 2: 17-alpha-ethinylestradiols (EE2)</b></p>
</div>

## Clean Heat project

The **2023 iGEM Ionis team** has set up the following challenge: to develop a **double-aimed system** that can produce **heat** for households at a low environmental impact, and at the same time **depollute water** from endocrine disruptors, such as EE2 (Fig. 3).

![]()

<div class="image_frame">
    <img class="illustration medium" src="https://static.igem.wiki/teams/4577/wiki/project/description/figure-3.svg">
    <p><b>Figure 3: Clean Heat system. Polluted wastewater or urines are driven from the residential area to a sequestered photobioreactor.</b> Ethinylestradiols are actively bioremediated and heat is bioproduced. The generated heat is transferred to a heat pump and amplified for residential area, while the depolluted water is redirected to the wastewater treatment plant to follow its treatment process.</p>
</div>

This system with a double function is made possible thanks to a coculture of genetically engineered **microalgae** and **bacteria** in a mutualistic interaction.

<div class="image_frame">
    <img class="illustration small" src="https://static.igem.wiki/teams/4577/wiki/project/description/mas-1.png">
</div>

## The science behind the project

### 1. Chlamy, our heat producer

The cabbage *Symplocarpus foetidus* has the capacity to produce a temperature differential of 35°C, thanks to its srAOX thermogenic protein, located in the inner mitochondrial membrane [1].

<div class="image_frame">
    <img class="illustration medium" src="https://static.igem.wiki/teams/4577/wiki/project/description/figure-4.jpg">
    <p><b>Figure 4: Diagrammatic representation of the plant mitochondrial electron transport chain and energy dissipation systems [1]</b>. I (complex I); nicotinamide adenine dinucleotide reduced (NADH) dehydrogenase; II (complex II), succinate dehydrogenase; III (complex III), oxidoreductase ubiquonol-cytochrome c; IV (complex IV), cytochrome c oxidase; AOX, alternative oxidase; c, cytochrome c; UQ, ubiquinone; V, (complex V) adenosine tri-phosphate (ATP) synthase; PUMP, plant uncoupling mitochondrial protein; μH+, proton electrochemical gradient built by the cytochrome pathway; NDex, external NAD(P)H dehydrogenases; NDin internal NAD(P)H dehydrogenases; IMS, intermembrane space; IMM, inner mitochondrial membrane. The cytochrome electron transport chain is shown in white. The energy dissipation systems are shown in black.</p>
</div>

![]()

This protein is mainly used as a security alternative pathway in case of imbalance in the mitochondrial chain. It is done by taking of the mitochondrial chain 2 electrons from the ubiquinone (between the protein 1 and 2), 2 protons from the mitochondrial matrix, and a dioxygen molecule to form water and heat. AOX has two main post-translational modifications [6]: oxidized or reduced. In fact, AOX is a protein dimer with is associated thanks to a di-sulfur bridge between two cysteines. This bond can be reduced, if so it activates AOX [6,7]. More interestingly, pyruvate can integrate into the dimer and stabilized the reduced form of AOX [8]. In other words, pyruvate may be used to activate AOX exogenous heat-producing capacity.

![]()

<div class="image_frame">
    <img class="illustration small" src="https://static.igem.wiki/teams/4577/wiki/project/description/mas-2.png">
</div>

This gene of interest can be transferred to *C. reinhardtii*, Chlamy for short. It is completed with introns, to avoid gene silencing, and with a peptide signal to direct to the mitochondrial membrane. *C. reinhardtii* was sequenced by Ito-Inaba in 2009 [9] and is well-known for its depolluting capacities. It would enable a final heat output of 60°C by combining this biotechnology with a heat pump system. This performance meets technical expectations since sanitary water must reach at least 55°C for killing most microorganisms.

### 2. Meli, our EE2-depolluting fighter

In 2021, **HiZJU-China iGEM team** has achieved the reduction of EE2 concentration by half by degrading this molecule into inactive components using two genes, amoA and HAO, inserted in Escherichia coli bacteria [5]. They have achieved the deactivation of 50% of EE2 in 48h of incubation.

*Bacteria + NH3 + EE2 --> Bacteria + NH2OH + OH-EE2*

<div class="image_frame">
    <img class="illustration medium" src="https://static.igem.wiki/teams/4577/wiki/project/description/figure-5.png">
    <p>Figure 5: Effect of modified E. Coli on EE2 [10]</p>
</div>

![]()

By harnessing the efficient oxidation of EE2 by amoA and the collaborative degradation of NH4+ by AMOA-HAO, the iiGEM achieved a treatment of nitrogen components in wastewater. Simultaneously, they utilized the oxidation process of heterotrophic bacteria to prevent EE2 degradation caused by insufficient NH4+ content, ensuring the monitoring of ee2-OH up to a specific extent.

In our project, these genes are transferred in *Ensifer meliloti*, a Gram-negative bacterium.

<div class="image_frame">
    <img class="illustration small" src="https://static.igem.wiki/teams/4577/wiki/project/description/mas-3.png">
</div>

This specific strain has been chosen for completing a particular aspect of the project: the coculture.

By naturally synthesizing vitamin B12, *S. meliloti* has shown to increase *C. reinhardtii* heat resistance [11] up to 42°C. It will enable our microorganisms to grow in higher temperatures.

<div class="image_frame">
    <img class="illustration big" src="https://static.igem.wiki/teams/4577/wiki/project/description/figure-6.svg">
    <p>
        Figure 6: Summary of the genetic modifications and the different interactions.<br>
        A) <i>C. reinhardtii</i> is modified with a plasmid having sraox gene. By producing srAOX, reduction and stabilization by pyruvate, <i>C. reinhardtii</i> will produce heat (exothermic reaction -ΔH).<br>
        B) <i>S. meliloti</i> is modified with a plasmid containing amoA and hao genes to enable the deactivation of EE2 and NH3.<br>
        C) Finally, <i>S. meliloti</i> naturally produced vitamin B12 to improve <i>C. reinhardtii</i> heat resistance.
    </p>
</div>

## The system more in depth

We had to imagine a system that can simultaneously retrieve the wastewater of the houses, stock them, redirect them to a photobioreactor until EE2 are deactivated and heat is generated. This heat then is transferred to a heat pump, to amplify and deliver a 55°C temperature for house and water heating.

![]()

<div class="image_frame">
    <img class="illustration big" src="https://static.igem.wiki/teams/4577/wiki/project/description/figure-7.svg">
    <p>Figure 7: Representation of our system connected to a heat pump.</p>
</div>

For this, we have created a prototype ([Link to Hardware](https://2023.igem.wiki/ionis-paris/hardware)) and modelized our system ([Link to model](https://2023.igem.wiki/ionis-paris/model)).

<div class="image_frame">
    <img class="illustration medium" src="https://static.igem.wiki/teams/4577/wiki/project/description/sans-cache.jpeg">
</div>

To make sure our system is safe and that microorganisms would not be able to leave the system, we implemented membrane filters at the entry and exit of the photobioreactor and we imagined a way to genetically modify our microorganisms with a kill switch to ensure a biological barrier ([Link to Safety](https://2023.igem.wiki/ionis-paris/safety)).

## Conclusion

In conclusion, our Clean Heat system represents a significant breakthrough in sustainable technology. Not only does it generate heat efficiently, but it also plays a double role in depolluting water from EE2 and utilizing wastewater, contributing to the revalorization of urine and biomass. Moreover, we assessed our carbon footprint impact with ClimatePartner to evaluate the sustainability of our project. Throughout our journey, we have actively engaged with stakeholders, seeking their insights to refine our project and align it with real-world needs ([Link to Human Practices](https://2023.igem.wiki/ionis-paris/human-practices)).

![]()

<div class="image_frame">
    <img class="illustration medium" src="https://static.igem.wiki/teams/4577/wiki/project/description/conclusion-1.jpg">
</div>

We invite you to go more deeply into our wiki to discover our project more in depth.

**Clean Heat, green it!**

## Sources:

[1]: Zhu, Yan et al. 2011. « Regulation of Thermogenesis in Plants: The Interaction of Alternative Oxidase and Plant Uncoupling Mitochondrial Protein ». Journal of Integrative Plant Biology 53(1): 7‑13.

[2]: Ademe (2021) Comment mieux se chauffer ?

[3]: United Nations (2019) Contraceptive Use by Method 2019: Data Booklet. UN. Available at: [https://doi.org/10.18356/1bd58a10-en](https://doi.org/10.18356/1bd58a10-en).

[4]: « Contraception : les pratiques des Françaises, pilule | vie-publique.fr ». 2017. [http://www.vie-publique.fr/en-bref/19725-contraception-les-pratiques-des-francaises-pilule](http://www.vie-publique.fr/en-bref/19725-contraception-les-pratiques-des-francaises-pilule)

[5]: Commission internationale pour la Protection du Rhin (2011). Rapport d’évaluation sur les œstrogènes.

[6]: Moore, Anthony L. et al. 2013. « Unraveling the Heater: New Insights into the Structure of the Alternative Oxidase ». Annual Review of Plant Biology 64(1): 637‑63.

[7]: Raven, John A., John Beardall, et Antonietta Quigg. 2020. « Light-Driven Oxygen Consumption in the Water-Water Cycles and Photorespiration, and Light Stimulated Mitochondrial Respiration ». 

[8]: Carré, J. E., C. Affourtit, et A. L. Moore. 2011. « Interaction of Purified Alternative Oxidase from Thermogenic Arum Maculatum with Pyruvate ». FEBS letters 585(2): 397‑401.

[9]: Ito-Inaba, Y., Hida, Y. and Inaba, T. (2009) ‘What is critical for plant thermogenesis? Differences in mitochondrial activity and protein expression between thermogenic and non-thermogenic skunk cabbages’, Planta, 231(1), pp. 121–130. Available at: [https://doi.org/10.1007/s00425-009-1034-z](https://doi.org/10.1007/s00425-009-1034-z).

[10]: Team:HiZJU-China - [2021.igem.org](https://2021.igem.org/) (no date). Available at: [https://2021.igem.org/Team:HiZJU-China](https://2021.igem.org/Team:HiZJU-China) (Accessed: 12 March 2023).

[11]: Xie, B. et al. (2013) ‘Chlamydomonas reinhardtii thermal tolerance enhancement mediated by a mutualistic interaction with vitamin B12-producing bacteria’, The ISME Journal, 7(8), pp. 1544–1555. Available at: [https://doi.org/10.1038/ismej.2013.43](https://doi.org/10.1038/ismej.2013.43).


        `}
      </PageContent>
    </div>
  );
}

export default Description;
